import React, { useEffect, useState, useImperativeHandle } from 'react';
import { connect } from 'react-redux';
import { useI18n } from '../../../../../i18n';
import { actions as processorActions } from '../../../../redux/api/processors';
import { actions as widgetsActions } from '../../../../redux/app/widgets';
import { actions as registryActions } from '../../../../redux/api/registry';
import ListItem from '../../../general/list/ListItem';
import AttributeRangeDetectorPicker from '../../attributeRangeDetectors/AttributeRangeDetectorPicker';
import iconOk from '../../../../img/widgets/device-presence-ok.png';
import iconIssue from '../../../../img/widgets/device-presence-issue.png';
import AttributeRangeDetectorControl from '../../attributeRangeDetectors/AttributeRangeDetectorControl';
import DeviceAssetTypeIcon from '../../../share/devices/DeviceAssetTypeIcon';
import { className, fromProps } from '../../../../lib/className';
import NoDataView from '../../../general/view/NoDataView';
import { useAttributeDescription } from 'hooks/attribute-range-detectors/useAttributeDescription';
import { useAttribute } from 'hooks/attribute-range-detectors/useAttribute';

import './xDetectorWidget.scss';


const matchesActions = processorActions.attributeRangeDetectorMatches;
const actions = processorActions.attributeRange;

/**
 * @param {Object} props
 * @param {cx.ods.processors.AttributeRangeDetector} props.detector
 * @param {boolean} [props.hasHeader]
 */

function _DetectorDetails(props) {
	const { f } = useI18n();

	const { messageField, attributeType } = useAttribute(props.detector.fieldId);
	const attributeRangeDescription = useAttributeDescription(props.detector, attributeType);

	useEffect(() => {
		if (props.messageFields.typeMap == null && !props.messageFields.pending) {
			props.dispatch(registryActions.messageFields.request());
		}
		if (props.attributeTypes.codeMap == null && !props.attributeTypes.pending) {
			props.dispatch(registryActions.attributeTypes.request());
		}
	}, []);

	let content = null;
	if (props.messageFields.typeMap != null && props.attributeTypes.codeMap != null) {
		content = (<>
			{props.hasHeader && <div className="header">{f('current')} {messageField?.name || ''} {f('violations')}</div>}
			<div className="content">
				{f('valid range')}: {attributeRangeDescription}
			</div>
		</>);
	}

	return (
		<div className="detector-details">
			{content}
		</div>
	)
}

const DetectorDetails = connect(state => {
	return {
		messageFields: state.registry.messageFields,
		attributeTypes: state.registry.attributeTypes
	}
})(_DetectorDetails);

/**
 * @param {Object} props
 * @param {DeviceDetailsProxy} props.device
 * @param {cx.ods.processors.AttributeRangeMatch} props.match
 */

function _DeviceListItem(props) {
	return (
		<ListItem className={className(fromProps(props))}>
			<div className="icon">
				<DeviceAssetTypeIcon categoryIds={props.device && props.device.categoryIds} />
			</div>
			<div className="content">
				<div>{props.device && props.device.denomination()}</div>
				<div>{props.match.detectedAt.toLocaleString()}</div>
			</div>
		</ListItem>
	)
}

const DeviceListItem = connect(
	(state, props) => ({
		device: state.devices.map && state.devices.map[props.match.uri]
	})
)(_DeviceListItem);


/**
 * @param {Object} props
 * @param {string} props.uid
 * @param {React.RefObject} props.customRef
 * @param {function} props.update to rerender WidgetContainer
 */

function AttributeRangeDetectorWidget(props) {
	const { f, fc, p } = useI18n();
	const widgetData = props.widget && props.widget.data;
	const _processorId = widgetData && widgetData.processorId;
	const [processorId, setProcessorId] = useState(_processorId || null);

	const outOfRange = [];
	const inRange = [];

	let detector = null;
	if (props.detectors.map != null && _processorId != null) {
		detector = props.detectors.map[_processorId];
	}

	useEffect(() => {
		if (props.detectors.map == null) {
			props.dispatch(actions.load.request());
		}
	}, []);

	useEffect(() => {
		if (_processorId != null) {
			props.dispatch(matchesActions.subscribe({ processorId: _processorId }));
		}
		return () => {
			if (_processorId != null) {
				props.dispatch(matchesActions.unsubscribe({ processorId: _processorId }));
			}
		}
	}, [_processorId]);

	useEffect(() => {
		if (processorId != null) {
			props.dispatch(widgetsActions.update({ domain: props.reduxKey, uid: props.uid, data: { processorId } }));
		}
	}, [processorId]);

	useEffect(() => {
		if (detector != null) {
			props.dispatch(matchesActions.matches.request({ processorId: _processorId }));
		}
	}, [detector]);


	const matches = _processorId != null
		&& props.matches[_processorId]
		&& props.matches[_processorId].matches
	;

	useEffect(() => {
		props.update();
	}, [matches]);

	let content = null;
	if (detector != null) {
		if (matches != null && matches.length > 0) {
			matches.forEach(match => {
				if (match.inRange) {
					inRange.push(match);
				} else {
					outOfRange.push(match);
				}
			});
			content = (<>
				<div className="indicator">
					{outOfRange.length > 0 &&
						<img src={iconIssue} alt="" />
					}
					{outOfRange.length == 0 &&
						<img src={iconOk} alt="" />
					}
				</div>
				<div className="summary">
					{p('X issues', outOfRange.length)}
				</div>
				<DetectorDetails hasHeader={outOfRange.length > 0 && props.widget && props.widget.expanded} detector={detector} />
			</>);
		} else {
			content = <NoDataView />;
		}
	}

	const listItems = outOfRange.map(match => <DeviceListItem key={match.uri} className="x-detector-widget-list-item" match={match} />);

	useImperativeHandle(props.customRef, () => ({
		title: () => detector && detector.name || fc('select detector'),
		configuration: () => {
			return (
				<AttributeRangeDetectorControl
					value={_processorId}
					label={f('attribute range detector')}
					placeholder={f('select detector')}
				/>
			)
		},
		dynamicContent: () => {
			return (outOfRange.length > 0 && listItems);
		},
		expandable: matches != null && matches.length > 0 && outOfRange.length > 0
	}));

	return (
		<div className="x-detector-widget attribute-range">
			{detector == null &&
				<AttributeRangeDetectorPicker
					value={processorId}
					onChange={setProcessorId}
					placeholder={f('select detector')}
				/>
			}
			{content}
		</div>
	);
}

export default connect(
	(state, props) => ({
		widget: state.widgets[props.reduxKey].map[props.uid],
		detectors: state.processors.attributeRange,
		matches: state.processors.attributeRangeMatches,
	})
)(AttributeRangeDetectorWidget);
